import _ from 'lodash';
import { toBase64 } from '../../../shared/util/base64';
import convertPromiseToQueryReturnValue from '../../shared/convertPromiseToQueryReturnValue';
import { DeviceDataAPIResponse } from '../device.model';
import { DeviceAPI } from '../DeviceAPI';
import { DeviceStatusAdapter } from './adapter/DeviceStatusAdapter';
import { CommanderAPIService } from './CommanderAPIService';
import {
  DeviceStatusAirConnectAPIResponse,
  DeviceStatusAirConnectSummaryAPIResponse,
  DeviceStatusMapAPIResponse,
  DeviceStatusSummaryAPIResponse,
} from './deviceStatus.model';
export const CommanderAPI = DeviceAPI.injectEndpoints({
  endpoints: (build) => ({
    getDeviceByCloudConnectId: build.query<
      DeviceDataAPIResponse | undefined,
      string
    >({
      query: (cloudConnectId) => {
        return {
          url: `/commander/cloudConnectID/${cloudConnectId}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),

    getDeviceBySerialNumber: build.query<
      DeviceDataAPIResponse | undefined,
      string
    >({
      query: (serialNumber) => {
        return {
          url: `/commander/serialnum/${toBase64(serialNumber)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    getDeviceById: build.query<DeviceDataAPIResponse | undefined, string>({
      query: (id) => {
        return {
          url: `/commander/${toBase64(id)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    setTimezoneCommander: build.mutation<
      string,
      { commanderId: string; timezone: string }
    >({
      query: ({ commanderId, timezone }) => {
        const encodedCommanderId = toBase64(commanderId);
        return {
          url: `config/${encodedCommanderId}`,
          method: 'put',
          data: [
            {
              key: 'device-timezone',
              value: timezone,
              type: 'TEXT',
            },
          ],
        };
      },
      invalidatesTags: ['device-actions'],
    }),
    getDevicesStatus: build.query({
      queryFn: (commanderIds: string[]) => {
        const response = new CommanderAPIService()
          // .getDevicesStatus(commanderIds)
          .getDevicesConnectionStatus(_.uniq(commanderIds))
          .then((data) =>
            DeviceStatusAdapter.setAll(
              DeviceStatusAdapter.getInitialState(),
              data
            )
          );
        return convertPromiseToQueryReturnValue(response);
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/workspace/:encodedWorkspaceID
    getDevicesConnectionStatusByWorkspace: build.query<
      DeviceStatusMapAPIResponse[] | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `/devicestate/connection/workspace/${toBase64(workspaceId)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/workspace/:encodedWorkspaceID/count
    getDevicesConnectionStatusCountByWorkspace: build.query<
      DeviceStatusSummaryAPIResponse | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `/devicestate/connection/workspace/${toBase64(
            workspaceId
          )}/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/group/:encodedGroupID
    getDevicesConnectionStatusByGroup: build.query<
      DeviceStatusMapAPIResponse[] | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `/devicestate/connection/group/${toBase64(groupId)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/group/:encodedGroupID/count
    getDevicesConnectionStatusCountByGroup: build.query<
      DeviceStatusSummaryAPIResponse | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `/devicestate/connection/group/${toBase64(groupId)}/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/user
    getDevicesConnectionStatusByUser: build.query<
      DeviceStatusMapAPIResponse[] | undefined,
      void
    >({
      query: () => {
        return {
          url: `/devicestate/connection/user`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/user/count
    getDevicesConnectionStatusCountByUser: build.query<
      DeviceStatusSummaryAPIResponse | undefined,
      void
    >({
      query: () => {
        return {
          url: `/devicestate/connection/user/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),

    // GET {{BaseURL}}/api/commander/airconnect/status/user/count
    getAirConnectStatusCountByUser: build.query<
      DeviceStatusAirConnectSummaryAPIResponse | undefined,
      void
    >({
      query: () => {
        return {
          url: `/commander/airconnect/status/user/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/group/:encodedGroupID/count
    getAirConnectStatusCountByGroup: build.query<
      DeviceStatusAirConnectSummaryAPIResponse | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `/commander/airconnect/status/group/${toBase64(groupId)}/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/workspace/:encodedWorkspaceID/count
    getAirConnectStatusCountByWorkspace: build.query<
      DeviceStatusAirConnectSummaryAPIResponse | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `/commander/airconnect/status/workspace/${toBase64(
            workspaceId
          )}/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/user
    getAirConnectStatusByUser: build.query<
      DeviceStatusAirConnectAPIResponse[] | undefined,
      void
    >({
      query: () => {
        return {
          url: `/commander/airconnect/status/user`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/group/:encodedGroupID
    getAirConnectStatusByGroup: build.query<
      DeviceStatusAirConnectAPIResponse[] | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `/commander/airconnect/status/group/${toBase64(groupId)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/workspace/:encodedWorkspaceID
    getAirConnectStatusByWorkspace: build.query<
      DeviceStatusAirConnectAPIResponse[] | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `/commander/airconnect/status/workspace/${toBase64(
            workspaceId
          )}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),

    // POST {{BaseURL}}/api/commander/airconnect/trigger/group/:encodedGroupID
    syncAirConnectStatusByGroup: build.mutation<void, string>({
      query: (groupId: string) => {
        return {
          url: `/commander/airconnect/trigger/group/${toBase64(groupId)}`,
          method: 'post',
        };
      },
      invalidatesTags: ['device-actions'],
    }),
  }),
});
